import React, { useEffect, useState } from 'react';
import { Alert, Button, LinearProgress, Stack, Typography, useMediaQuery } from '@mui/material';

import type { IntakeStatusBaseSteps } from '@shared/patient-intake-status';

import { CopyButton } from '../../../../components/ui/CopyButton.jsx';
import { usePatientIntakeStatusContext } from '../hooks';
import { BaseStepIcon } from './BaseStepIcon.tsx';

type AccessCodeProps = {
  accessCode: string;
  isMobile: boolean;
};

const VIEW_TIMEOUT = 3000;
const PROGRESS_INTERVAL = 100;
const NUMBER_OF_INTERVALS = Math.floor(VIEW_TIMEOUT / PROGRESS_INTERVAL) - 5; // -5 to account for the initial delay
const PROGRESS_INCREMENT = 100 / NUMBER_OF_INTERVALS;

const MobileAccessCode = ({ accessCode }: { accessCode: string }) => {
  const [viewState, setViewState] = useState<'view' | 'copy'>('view');
  const [progress, setProgress] = useState(100);

  useEffect(() => {
    if (viewState === 'view') {
      return;
    }

    const interval = setInterval(() => {
      setProgress((prev) => prev - PROGRESS_INCREMENT);
    }, PROGRESS_INTERVAL);

    setTimeout(() => {
      setViewState('view');
      setProgress(100);
      clearInterval(interval);
    }, VIEW_TIMEOUT);

    return () => clearInterval(interval);
  }, [viewState]);

  const handleView = () => {
    setViewState('copy');
  };

  const accessCodeDisplay =
    viewState === 'view' ? Array.from({ length: accessCode.length }, () => '*').join('') : accessCode;

  if (viewState === 'view') {
    return (
      <Button fullWidth variant="outlined" onClick={handleView}>
        View Code
      </Button>
    );
  }

  return (
    <Stack width="100%" gap={0.5}>
      <CopyButton fullWidth variant="outlined" textToCopy={accessCode} alwaysShowIcon sx={{ width: '100%' }}>
        <Typography variant="body2" fontWeight="bold" fontFamily="monospace">
          {accessCodeDisplay}
        </Typography>
      </CopyButton>

      <LinearProgress variant="determinate" value={progress} />
    </Stack>
  );
};

const AccessCode: React.FC<AccessCodeProps> = ({ accessCode, isMobile }) => {
  const [isHovering, setIsHovering] = useState(false);

  const handleMouseEnter = () => {
    setIsHovering(true);
  };

  const handleMouseLeave = () => {
    setIsHovering(false);
  };

  if (isMobile) {
    return <MobileAccessCode accessCode={accessCode} />;
  }

  const accessCodeDisplay =
    !isHovering && !isMobile ? Array.from({ length: accessCode.length }, () => '*').join('') : accessCode;

  return (
    <CopyButton
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      textToCopy={accessCode}
      noHighlight
      iconOnLeft
      variant="text"
      color="inherit"
    >
      <Typography variant="body2" fontWeight="bold" fontFamily="monospace">
        {accessCodeDisplay}
      </Typography>
    </CopyButton>
  );
};

type HealthcareProviderSectionProps = {
  step: IntakeStatusBaseSteps['MHR_ACCESS'];
};

const HealthcareProviderSection = ({ step }: HealthcareProviderSectionProps) => {
  const { isHealthcareProvider } = usePatientIntakeStatusContext();

  if (!isHealthcareProvider) {
    return null;
  }

  if (step.currentStatus.status === 'COMPLETED') {
    return null;
  }

  return (
    <Alert severity="warning" icon={false}>
      Please verify the patient has provided access to their My Health Record by using the code provided.
    </Alert>
  );
};

type MHRAccessViewerProps = {
  step: IntakeStatusBaseSteps['MHR_ACCESS'];
};

export const MHRAccessViewer: React.FC<MHRAccessViewerProps> = ({ step }) => {
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('sm'));

  if (!step) {
    return null;
  }

  const { metadata } = step.currentStatus;

  if (!metadata) {
    return null;
  }

  const { accessCode, accessCodeProvidedAt } = metadata;

  return (
    <Stack direction="column" gap={0.5}>
      <Stack
        direction={isMobile ? 'column' : 'row'}
        alignItems="center"
        justifyContent="space-between"
        gap={isMobile ? 1 : 4}
      >
        <Stack direction="row" alignItems="center" gap={1}>
          <BaseStepIcon step={step} color="success" fontSize="small" />
          <Typography variant="body2" fontWeight="bold">
            MHR Access Code Provided
          </Typography>
        </Stack>
        <AccessCode accessCode={accessCode} isMobile={isMobile} />
      </Stack>
      {accessCodeProvidedAt && (
        <Typography variant="caption">
          The code was provided on{' '}
          {accessCodeProvidedAt.toDate().toLocaleDateString('en-AU', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
          })}
        </Typography>
      )}

      <HealthcareProviderSection step={step} />
    </Stack>
  );
};
